import { supabase, toAppError } from '@/lib/db/supabase'
import type { OrgRole } from '@/lib/org/org-context'
import type { InviteOutcome, InviteStatus, PendingInvite } from './invite-types'

interface InviteRow {
  id: string
  email: string
  role: OrgRole
  status: InviteStatus
  invited_by: string | null
  invited_by_email: string | null
  expires_at: string
  last_sent_at: string | null
  send_count: number
  accepted_at: string | null
  revoked_at: string | null
  created_at: string
}

function toInvite(row: InviteRow): PendingInvite {
  return {
    id: row.id,
    email: row.email,
    role: row.role,
    status: row.status,
    invitedBy: row.invited_by,
    invitedByEmail: row.invited_by_email,
    expiresAt: row.expires_at,
    lastSentAt: row.last_sent_at,
    sendCount: row.send_count,
    acceptedAt: row.accepted_at,
    revokedAt: row.revoked_at,
    createdAt: row.created_at,
  }
}

/** The RPC returns jsonb; normalise it once here rather than at every call site. */
function toOutcome(data: unknown): InviteOutcome {
  const d = (data ?? {}) as Record<string, unknown>
  return {
    outcome: (d.outcome as InviteOutcome['outcome']) ?? 'invited',
    userId: (d.user_id as string | null) ?? null,
    inviteId: (d.invite_id as string | null) ?? null,
    email: (d.email as string) ?? '',
    role: (d.role as OrgRole) ?? 'viewer',
    organizationId: (d.organization_id as string) ?? '',
    organizationName: (d.organization_name as string | null) ?? null,
    expiresAt: (d.expires_at as string | null) ?? null,
  }
}

export interface MyInvite {
  id: string
  organizationId: string
  organizationName: string
  role: OrgRole
  expiresAt: string
  invitedByEmail: string | null
}

export const inviteRepository = {
  /** Invitations for a company. Any member may read them, as with the roster. */
  async list(orgId: string, includeHistory = false): Promise<PendingInvite[]> {
    const { data, error } = await supabase.rpc('list_invites', {
      p_org: orgId,
      p_include_history: includeHistory,
    })
    if (error) throw new Error(toAppError(error).message)
    return ((data ?? []) as InviteRow[]).map(toInvite)
  },

  /**
   * Adds an existing user, or records an invitation when the address has no
   * account yet. The caller reads `outcome` to decide what to say.
   */
  async create(orgId: string, email: string, role: OrgRole): Promise<InviteOutcome> {
    const { data, error } = await supabase.rpc('invite_member', {
      p_org: orgId,
      p_email: email.trim(),
      p_role: role,
    })
    if (error) throw new Error(toAppError(error).message)
    return toOutcome(data)
  },

  async revoke(inviteId: string): Promise<void> {
    const { error } = await supabase.rpc('revoke_invite', { p_invite: inviteId })
    if (error) throw new Error(toAppError(error).message)
  },

  /**
   * Renews an invitation — and repairs one whose issuer lost the rank that
   * authorised it. Returns `outcome: 'added'` when the person turned out to
   * have signed up in the meantime, in which case they are now a member.
   */
  async resend(inviteId: string): Promise<InviteOutcome> {
    const { data, error } = await supabase.rpc('resend_invite', { p_invite: inviteId })
    if (error) throw new Error(toAppError(error).message)
    return toOutcome(data)
  },

  /** The invitee's own "no thanks". */
  async decline(inviteId: string): Promise<void> {
    const { error } = await supabase.rpc('decline_invite', { p_invite: inviteId })
    if (error) throw new Error(toAppError(error).message)
  },

  /** Invitations addressed to the signed-in user, for /accept-invite. */
  async mine(): Promise<MyInvite[]> {
    const { data, error } = await supabase.rpc('my_pending_invites')
    if (error) throw new Error(toAppError(error).message)
    return ((data ?? []) as Array<{
      id: string
      organization_id: string
      organization_name: string
      role: OrgRole
      expires_at: string
      invited_by_email: string | null
    }>).map((row) => ({
      id: row.id,
      organizationId: row.organization_id,
      organizationName: row.organization_name,
      role: row.role,
      expiresAt: row.expires_at,
      invitedByEmail: row.invited_by_email,
    }))
  },

  /**
   * Claims every invitation addressed to the signed-in user's confirmed
   * address. Returns how many were claimed.
   *
   * The database triggers are the primary path; this is the recovery for the
   * cases they cannot cover — the user signed up before this feature shipped,
   * their confirmation happened by a route we did not anticipate, or they
   * changed their address to the invited one. Cheap enough to call on boot: it
   * is one index-only lookup when there is nothing to claim.
   */
  async claimMine(): Promise<number> {
    const { data, error } = await supabase.rpc('claim_my_invites')
    if (error) throw new Error(toAppError(error).message)
    return typeof data === 'number' ? data : 0
  },
}
